/* Clinq wordmark with an amber pulsing dot above the `i` (replacing its tittle).
 * - `size`: "xl" | "lg" | "md" | "sm"   (controls font size + dot size)
 * - `tone`: "teal" | "white" | "amber"  (wordmark colour; dot adapts for contrast)
 * - `className`: optional extra classes for the outer span
 *
 * Implementation: we render "Cl" + dotless-i (U+0131) + "nq" and overlay an amber
 * dot exactly where the i's natural tittle would sit, so the dot becomes the i-dot.
 */
const SIZE_MAP = {
  xl: { text: "text-[44px]", dot: "h-3 w-3"     },
  lg: { text: "text-2xl",    dot: "h-2 w-2"     },
  md: { text: "text-xl",     dot: "h-2 w-2"     },
  sm: { text: "text-base",   dot: "h-1.5 w-1.5" },
};

const TONE_MAP = {
  teal:  { text: "text-[#0A8B85]", dot: "bg-[#F59E0B]", halo: "rgba(245, 158, 11, 0.55)" },
  white: { text: "text-white",     dot: "bg-[#F59E0B]", halo: "rgba(245, 158, 11, 0.6)"  },
  amber: { text: "text-[#F59E0B]", dot: "bg-[#0A8B85]", halo: "rgba(10, 139, 133, 0.6)"  },
};

export const ClinqLogo = ({ size = "lg", tone = "teal", className = "", testid = "clinq-logo" }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.lg;
  const t = TONE_MAP[tone] || TONE_MAP.teal;
  return (
    <span
      data-testid={testid}
      className={"clinq-logo relative inline-flex items-baseline font-extrabold tracking-tight " + s.text + " " + t.text + " " + className}
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", "--clinq-halo": t.halo }}
    >
      Cl
      <span className="relative inline-block">
        {"\u0131"}{/* dotless i — U+0131 */}
        <span
          aria-hidden="true"
          className={"clinq-dot absolute left-1/2 rounded-full " + t.dot + " " + s.dot}
          style={{ bottom: "1.05em" }}
        />
      </span>
      nq
    </span>
  );
};

/* Global pulse keyframes — injected once at app boot. Halo colour is per-instance via --clinq-halo. */
export const ClinqLogoStyles = () => (
  <style>{`
    .clinq-logo { --clinq-halo: rgba(245, 158, 11, 0.55); }
    .clinq-dot {
      box-shadow: 0 0 0 0 var(--clinq-halo);
      animation: clinq-pulse 2.4s ease-out infinite;
    }
    @keyframes clinq-pulse {
      0%   { box-shadow: 0 0 0 0   var(--clinq-halo); transform: translateX(-50%) scale(1);    }
      70%  { box-shadow: 0 0 0 12px transparent;     transform: translateX(-50%) scale(1.18); }
      100% { box-shadow: 0 0 0 0   transparent;      transform: translateX(-50%) scale(1);    }
    }
    @media (prefers-reduced-motion: reduce) {
      .clinq-dot { animation: none; }
    }
  `}</style>
);

export default ClinqLogo;
